import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, message, Space } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SendOutlined, SyncOutlined } from '@ant-design/icons';
import moment from 'moment';
import { brokerAPI } from '../services/api';

const BrokerAccounts = ({ broker }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [deletingRecord, setDeletingRecord] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await brokerAPI.list(broker);
      setData(response.data || []);
    } catch (error) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshLongPortToken = (record) => {
    if (!record?.id) return;
    Modal.confirm({
      title: '确认刷新 LongPort Access Token',
      content: `将为账户「${record?.name || record?.id}」刷新 Access Token，并写回数据库，是否继续？`,
      okText: '确认刷新',
      cancelText: '取消',
      onOk: async () => {
        setRefreshingId(record.id);
        try {
          const res = await brokerAPI.refreshLongPortToken(record.id);
          const data = res?.data || {};
          message.success('刷新 Token 成功');
          Modal.info({
            title: '刷新结果',
            width: 700,
            content: <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>,
          });
          fetchData();
        } catch (error) {
          message.error(error?.response?.data?.detail || '刷新 Token 失败');
        } finally {
          setRefreshingId(null);
        }
      },
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [broker]);

  const handleCreate = () => {
    setEditingRecord(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = (record) => {
    setDeletingRecord(record);
    setIsDeleteModalVisible(true);
  };

  const handleTest = async (record) => {
    if (!record?.id) return;
    setTestingId(record.id);
    try {
      const res = await brokerAPI.test(broker, record.id);
      const data = res?.data ?? {};
      if (data?.ok) {
        message.success('测通成功');
      } else {
        message.error('测通失败');
      }
      Modal.info({
        title: '测通返回',
        width: 700,
        content: <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>,
      });
    } catch (error) {
      const data = error?.response?.data ?? {
        ok: false,
        error: error?.message || 'request failed',
      };
      message.error('测通失败');
      Modal.info({
        title: '测通返回',
        width: 700,
        content: <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>,
      });
    } finally {
      setTestingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingRecord) return;

    try {
      await brokerAPI.delete(broker, deletingRecord.id);
      message.success('删除成功');
      setIsDeleteModalVisible(false);
      setDeletingRecord(null);
      fetchData();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalVisible(false);
    setDeletingRecord(null);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingRecord) {
        await brokerAPI.update(broker, editingRecord.id, values);
        message.success('更新成功');
      } else {
        await brokerAPI.create(broker, values);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchData();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const columnsByBroker = () => {
    const common = [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
      { title: '别名', dataIndex: 'name', key: 'name', width: 150 },
    ];

    const tail = [
      { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 160, render: (date) => (date ? moment.utc(date).utcOffset(8).format('YYYY-MM-DD HH:mm:ss') : '-') },
      { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at', width: 160, render: (date) => (date ? moment.utc(date).utcOffset(8).format('YYYY-MM-DD HH:mm:ss') : '-') },
      {
        title: '操作',
        key: 'action',
        fixed: 'right',
        width: broker === 'long_port' ? 320 : 220,
        render: (_, record) => (
          <Space>
            <Button type="link" icon={<SendOutlined />} loading={testingId === record.id} onClick={() => handleTest(record)}>
              测通
            </Button>
            {broker === 'long_port' && (
              <Button type="link" icon={<SyncOutlined />} loading={refreshingId === record.id} onClick={() => handleRefreshLongPortToken(record)}>
                刷新Token
              </Button>
            )}
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
              编辑
            </Button>
            <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
              删除
            </Button>
          </Space>
        ),
      },
    ];

    if (broker === 'futu' || broker === 'moomoo') {
      return [...common, { title: '券商类型', dataIndex: 'security_firm', key: 'security_firm', width: 140 }, { title: 'HOST', dataIndex: 'host', key: 'host', width: 140 }, { title: 'PORT', dataIndex: 'port', key: 'port', width: 90 }, { title: '证书路径', dataIndex: 'rsa_file_path', key: 'rsa_file_path', width: 220 }, { title: 'ACC_ID', dataIndex: 'acc_id', key: 'acc_id', width: 140 }, ...tail];
    }

    if (broker === 'long_port') {
      return [...common, { title: 'APP_KEY', dataIndex: 'app_key', key: 'app_key', width: 220 }, ...tail];
    }

    if (broker === 'tiger') {
      return [...common, { title: 'TIGER_ID', dataIndex: 'tiger_id', key: 'tiger_id', width: 160 }, { title: 'ACCOUNT', dataIndex: 'account', key: 'account', width: 160 }, ...tail];
    }

    if (broker === 'ib') {
      return [...common, { title: 'HOST', dataIndex: 'host', key: 'host', width: 140 }, { title: 'PORT', dataIndex: 'port', key: 'port', width: 90 }, { title: 'client_id', dataIndex: 'client_id', key: 'client_id', width: 100 }, ...tail];
    }

    return [...common, ...tail];
  };

  const formItemsByBroker = () => {
    const common = [
      <Form.Item key="name" name="name" label="别名" rules={[{ required: true, message: '请输入别名' }]}>
        <Input />
      </Form.Item>,
    ];

    if (broker === 'futu' || broker === 'moomoo') {
      return [
        ...common,
        <Form.Item key="security_firm" name="security_firm" label="券商类型" rules={[{ required: true }]}>
          <Input />
        </Form.Item>,
        <Form.Item key="host" name="host" label="HOST" rules={[{ required: true }]}>
          <Input />
        </Form.Item>,
        <Form.Item key="port" name="port" label="PORT" rules={[{ required: true }]}>
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>,
        <Form.Item key="trade_password" name="trade_password" label="交易密码">
          <Input.Password />
        </Form.Item>,
        <Form.Item key="rsa_file_path" name="rsa_file_path" label="证书路径">
          <Input />
        </Form.Item>,
        <Form.Item key="acc_id" name="acc_id" label="ACC_ID">
          <Input />
        </Form.Item>,
      ];
    }

    if (broker === 'long_port') {
      return [
        ...common,
        <Form.Item key="app_key" name="app_key" label="APP_KEY" rules={[{ required: true }]}>
          <Input />
        </Form.Item>,
        <Form.Item key="app_secret" name="app_secret" label="APP_SECRET">
          <Input.Password />
        </Form.Item>,
        <Form.Item key="access_token" name="access_token" label="ACCESS_TOKEN">
          <Input.Password />
        </Form.Item>,
      ];
    }

    if (broker === 'tiger') {
      return [
        ...common,
        <Form.Item key="tiger_id" name="tiger_id" label="TIGER_ID" rules={[{ required: true }]}>
          <Input />
        </Form.Item>,
        <Form.Item key="account" name="account" label="ACCOUNT" rules={[{ required: true }]}>
          <Input />
        </Form.Item>,
        <Form.Item key="private_key_pk1" name="private_key_pk1" label="PRIVATE_KEY_PK1">
          <Input.Password />
        </Form.Item>,
      ];
    }

    if (broker === 'ib') {
      return [
        ...common,
        <Form.Item key="host" name="host" label="HOST" rules={[{ required: true }]}>
          <Input />
        </Form.Item>,
        <Form.Item key="port" name="port" label="PORT" rules={[{ required: true }]}>
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>,
        <Form.Item key="client_id" name="client_id" label="client_id">
          <InputNumber style={{ width: '100%' }} placeholder="可选，不填则系统自动分配" />
        </Form.Item>,
      ];
    }

    return common;
  };

  return (
    <div>
      <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} style={{ marginBottom: 16 }}>
        添加账户
      </Button>

      <Table columns={columnsByBroker()} dataSource={data} rowKey="id" loading={loading} scroll={{ x: 1400 }} />

      <Modal title={editingRecord ? '编辑账户' : '添加账户'} open={modalVisible} onOk={handleSubmit} onCancel={() => setModalVisible(false)} destroyOnClose>
        <Form form={form} layout="vertical">
          {formItemsByBroker()}
        </Form>
      </Modal>

      <Modal title="确认删除" open={isDeleteModalVisible} onOk={handleConfirmDelete} onCancel={handleCancelDelete} okText="确认删除" cancelText="取消" okType="danger">
        <p>确定要删除券商账户 "{deletingRecord?.name}" 吗？</p>
        <p style={{ color: 'red', marginTop: '8px' }}>此操作无法撤销，删除后将永久失去该账户及其所有相关数据。</p>
      </Modal>
    </div>
  );
};

export default BrokerAccounts;
